import { TextField, InputAdornment, type SxProps, type Theme } from '@mui/material';

interface PriceInputProps {
  value: string | number;
  onChange: (value: string) => void;
  size?: 'small' | 'medium';
  label?: string;
  disabled?: boolean;
  required?: boolean;
  allowNegative?: boolean;
  sx?: SxProps<Theme>;
}

export function PriceInput({
  value,
  onChange,
  size = 'small',
  label,
  disabled = false,
  required = true,
  allowNegative = false,
  sx,
}: PriceInputProps) {
  const stringValue = value?.toString() ?? '';
  const isEmpty = stringValue.trim() === '';
  const showError = required && isEmpty;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    // Allow empty string, digits, decimal point, and optionally minus sign
    const pattern = allowNegative ? /^-?\d*\.?\d*$/ : /^\d*\.?\d*$/;
    if (newValue === '' || pattern.test(newValue)) {
      onChange(newValue);
    }
  };

  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    // Select all text on focus for easy replacement
    e.target.select();
  };

  return (
    <TextField
      size={size}
      type="text"
      inputMode="decimal"
      value={stringValue}
      onChange={handleChange}
      onFocus={handleFocus}
      disabled={disabled}
      label={label}
      error={showError}
      slotProps={{
        input: {
          startAdornment: <InputAdornment position="start">$</InputAdornment>,
        },
        htmlInput: {
          pattern: allowNegative ? '^-?\\d*\\.?\\d*$' : '^\\d*\\.?\\d*$',
          style: { textAlign: 'right' },
        },
      }}
      sx={{
        width: 100,
        '& .MuiOutlinedInput-root': {
          ...(showError && {
            '& fieldset': {
              borderColor: 'error.main',
              borderWidth: 2,
            },
            '&:hover fieldset': {
              borderColor: 'error.main',
            },
          }),
        },
        ...sx,
      }}
    />
  );
}
